// HECHTER PARIS – "Den Look vollenden" (Shop the Look)
// Pager + Express-Shop: Größe wählen -> Artikel direkt per AJAX in den Warenkorb, danach öffnet sich der Cart-Drawer.
// Optionen mit nur einem Wert (z. B. eine Farbe pro Produkt) werden automatisch gewählt.
import { CartAddEvent } from '@theme/events';

class HpDenLook extends HTMLElement {
  connectedCallback() {
    if (this._hpInit) return;
    this._hpInit = true;
    this.pageSize = Math.max(1, parseInt(this.dataset.pageSize || '3', 10));
    this.items = [...this.querySelectorAll('[data-item]')];
    this.pages = Math.max(1, Math.ceil(this.items.length / this.pageSize));
    this.page = 0;
    this.pager = this.querySelector('[data-pager]');
    if (this.pager && this.pages > 1) {
      this.pager.hidden = false;
      this.querySelector('[data-total]').textContent = String(this.pages);
      this.querySelector('[data-prev]').addEventListener('click', () => this.go(this.page - 1));
      this.querySelector('[data-next]').addEventListener('click', () => this.go(this.page + 1));
    }
    this.list = this.querySelector('.hp-den-look__list');
    this.go(0);
    this.items.forEach((item) => this.setupItem(item));
    this.initImages();

    // Höhe der Liste auf die höchste Seite festsetzen, damit die Seite beim Blättern nicht springt
    if (this.pages > 1 && this.list) {
      this.lockHeight();
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => this.lockHeight(), 150);
      });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.lockHeight());
    }
  }

  // Bilder einordnen: Freisteller mit hellem, gleichmäßigem Hintergrund werden mit Abstand in die Kachel
  // gesetzt und farblich an den Kachel-Hintergrund angeglichen (kein sichtbarer Rand). Alles andere füllt die Kachel.
  initImages() {
    this.querySelectorAll('.hp-den-look__image').forEach((img) => {
      const run = () => this.classifyImage(img);
      if (img.complete && img.naturalWidth) run();
      else {
        img.addEventListener('load', run, { once: true });
        img.addEventListener('error', () => img.closest('.hp-den-look__media')?.classList.add('is-ready'), { once: true });
      }
    });
  }

  classifyImage(img) {
    const media = img.closest('.hp-den-look__media');
    if (!media) return;
    try {
      const w = 48;
      const h = Math.max(2, Math.round((w * img.naturalHeight) / img.naturalWidth));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;
      // Alle Randpixel prüfen
      const border = [];
      for (let x = 0; x < w; x++) border.push(x, (h - 1) * w + x);
      for (let y = 1; y < h - 1; y++) border.push(y * w, y * w + w - 1);
      let transparent = 0;
      const light = [];
      border.forEach((i) => {
        const o = i * 4;
        if (d[o + 3] < 20) {
          transparent++;
          return;
        }
        if (Math.min(d[o], d[o + 1], d[o + 2]) >= 200) light.push([d[o], d[o + 1], d[o + 2]]);
      });
      const n = border.length;
      if (transparent / n >= 0.5) {
        media.classList.add('is-cutout');
      } else if (light.length / n >= 0.5) {
        const ref = [0, 1, 2].map((k) => light.reduce((s, p) => s + p[k], 0) / light.length);
        const lightest = Math.min(...ref);
        media.classList.add('is-cutout');
        media.style.setProperty('--hp-bright', Math.min(1.2, 255 / lightest).toFixed(3));
      } else {
        media.classList.add('is-photo');
      }
    } catch (e) {
      media.classList.add('is-photo');
    }
    media.classList.add('is-ready');
  }

  lockHeight() {
    if (!this.list) return;
    const current = this.page;
    this.list.style.minHeight = '';
    let max = 0;
    for (let p = 0; p < this.pages; p++) {
      this.items.forEach((item, i) => {
        item.hidden = Math.floor(i / this.pageSize) !== p;
      });
      max = Math.max(max, this.list.offsetHeight);
    }
    this.items.forEach((item, i) => {
      item.hidden = Math.floor(i / this.pageSize) !== current;
    });
    this.list.style.minHeight = `${max}px`;
  }

  go(page) {
    this.page = Math.min(Math.max(page, 0), this.pages - 1);
    this.items.forEach((item, i) => {
      item.hidden = Math.floor(i / this.pageSize) !== this.page;
    });
    if (this.pages > 1) {
      this.querySelector('[data-current]').textContent = String(this.page + 1);
      this.querySelector('[data-prev]').disabled = this.page === 0;
      this.querySelector('[data-next]').disabled = this.page === this.pages - 1;
    }
    this.closeAll();
  }

  setupItem(item) {
    let data;
    try {
      data = JSON.parse(item.querySelector('[data-product-json]').textContent);
    } catch (e) {
      return;
    }
    const ctx = {
      data,
      btn: item.querySelector('[data-express]'),
      picker: item.querySelector('[data-picker]'),
      msg: item.querySelector('[data-msg]'),
      chosen: [],
      manual: [],
    };
    if (!ctx.btn || !ctx.picker || !ctx.msg) return;

    ctx.btn.addEventListener('click', () => {
      this.clearMsg(ctx.msg);
      if (data.variants.length === 1) {
        this.add(data.variants[0], ctx);
        return;
      }
      const wasClosed = ctx.picker.hidden;
      this.closeAll();
      if (wasClosed) {
        ctx.chosen = [];
        ctx.manual = [];
        ctx.picker.hidden = false;
        ctx.btn.setAttribute('aria-expanded', 'true');
        this.renderStep(ctx);
        const first = ctx.picker.querySelector('.hp-den-look__value:not(:disabled)');
        if (first) first.focus();
      }
    });
  }

  clearMsg(msg) {
    msg.textContent = '';
    msg.classList.remove('hp-den-look__msg--error');
  }

  closeAll() {
    this.querySelectorAll('[data-picker]').forEach((p) => {
      p.hidden = true;
      p.innerHTML = '';
    });
    this.querySelectorAll('[data-express]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  matches(v, chosen) {
    return chosen.every((val, i) => v.options[i] === val);
  }

  valuesFor(data, chosen) {
    const step = chosen.length;
    const values = [];
    data.variants.forEach((v) => {
      if (this.matches(v, chosen) && !values.includes(v.options[step])) values.push(v.options[step]);
    });
    return values;
  }

  renderStep(ctx) {
    const { data, picker } = ctx;

    // Optionen mit nur einem möglichen Wert automatisch wählen
    while (ctx.chosen.length < data.options.length) {
      const vals = this.valuesFor(data, ctx.chosen);
      if (vals.length !== 1) break;
      ctx.chosen.push(vals[0]);
    }

    if (ctx.chosen.length >= data.options.length) {
      this.finish(ctx);
      return;
    }

    const step = ctx.chosen.length;
    const optionName = data.options[step] || '';
    const values = this.valuesFor(data, ctx.chosen);

    picker.innerHTML = '';
    const label = document.createElement('span');
    label.className = 'hp-den-look__step-label';
    label.textContent = `${optionName} ${this.dataset.tChoose || ''}`.trim();

    const wrap = document.createElement('div');
    wrap.className = 'hp-den-look__values';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', optionName);

    values.forEach((val) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hp-den-look__value';
      b.textContent = val;
      const anyAvailable = data.variants.some(
        (v) => this.matches(v, ctx.chosen) && v.options[step] === val && v.available
      );
      if (!anyAvailable) {
        b.disabled = true;
        b.setAttribute('aria-label', `${val} – ${this.dataset.tSoldout || ''}`);
      }
      b.addEventListener('click', () => {
        ctx.manual.push(ctx.chosen.length);
        ctx.chosen.push(val);
        b.setAttribute('aria-busy', 'true');
        this.renderStep(ctx);
        const next = picker.querySelector('.hp-den-look__value:not(:disabled)');
        if (next && !picker.hidden) next.focus();
      });
      wrap.appendChild(b);
    });

    picker.append(label, wrap);

    if (ctx.manual.length > 0) {
      const lastStep = ctx.manual[ctx.manual.length - 1];
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'hp-den-look__back';
      back.textContent = `← ${data.options[lastStep]}: ${ctx.chosen[lastStep]}`;
      back.addEventListener('click', () => {
        this.stepBack(ctx);
        this.renderStep(ctx);
      });
      picker.appendChild(back);
    }
  }

  stepBack(ctx) {
    const lastStep = ctx.manual.pop();
    ctx.chosen = lastStep === undefined ? [] : ctx.chosen.slice(0, lastStep);
  }

  finish(ctx) {
    const variant = ctx.data.variants.find((v) => v.options.every((o, i) => o === ctx.chosen[i]));
    ctx.picker.querySelectorAll('button').forEach((x) => (x.disabled = true));
    this.add(variant, ctx).then((ok) => {
      if (ok) {
        ctx.picker.hidden = true;
        ctx.picker.innerHTML = '';
        ctx.btn.setAttribute('aria-expanded', 'false');
        ctx.chosen = [];
        ctx.manual = [];
      } else {
        this.stepBack(ctx);
        this.renderStep(ctx);
      }
    });
  }

  async add(variant, ctx) {
    const { btn, msg } = ctx;
    if (!variant) return false;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((el) => el.dataset.sectionId)
          .filter(Boolean)
      ),
    ];
    btn.disabled = true;
    try {
      const body = { items: [{ id: variant.id, quantity: 1 }] };
      if (sectionIds.length) body.sections = sectionIds.join(',');
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || json.status) {
        throw new Error(json.description || json.message || this.dataset.tError);
      }
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const productId = json.items && json.items[0] ? String(json.items[0].product_id) : '';
      document.dispatchEvent(
        new CartAddEvent(cart, 'hp-den-look', {
          source: 'hp-den-look',
          itemCount: cart.item_count,
          productId,
          variantId: String(variant.id),
          sections: json.sections,
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && !drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
      msg.textContent = this.dataset.tAdded || '';
      return true;
    } catch (e) {
      msg.textContent = (e && e.message) || this.dataset.tError || '';
      msg.classList.add('hp-den-look__msg--error');
      return false;
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('hp-den-look')) customElements.define('hp-den-look', HpDenLook);
